import { useState, useEffect, useRef } from 'react'
import { AllStatusWrapper } from '../../../../components/execution/terminal/AllStatusWrapper'
import { PaymentStatus, TerminalStatus, TerminalStatusContext } from '../../../../types/mf-execution.types'
import { useOrderPaymentStatusQuery } from '../../../../hooks/mf-execution/useOrderPaymentStatusQuery'
import { postCTA, upiPay } from '../../../../utils/appBridge'
import { getOrdersTabCTA } from '../../../../utils/mutualFundDetail'
import { convertMillisecondsToMinutesAndSeconds } from '../../../../utils/utils'
import { useSearchParams } from 'react-router-dom'
import { useBottomSheet } from '../../../../state-management/contexts/BottomSheetContext'
import { PaymentNotCompletedBottomSheet } from '../../../../components/BottomSheetComponents/mf-execution/PaymentNotCompletedBottomSheet'
import { UpiIntentRedirectModal } from '../../../../components/execution/upi/UpiIntentRedirectModal'
import { isUpiIntentsFlowEnabled } from '../../../../utils/mf-execution/upiIntentsFeature'

export const BuyUPIPaymentStatusPage = () => {
  const upiIntentsEnabled = isUpiIntentsFlowEnabled()
  const [status, setStatus] = useState<TerminalStatus>(TerminalStatus.AWAITING)
  const [showUpiRedirectModal, setShowUpiRedirectModal] = useState(false)
  const [searchParams] = useSearchParams()
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const hasTriggeredUpiIntentRef = useRef(false)
  const orderId = searchParams.get('orderId')
  const displayAccNumber = searchParams.get('displayAccNumber') || 'your bank account'

  const { paymentStatus, isError, isExpired, timeLeft } = useOrderPaymentStatusQuery({ maxTimer: 120000 })

  const isUpiIntentAwaiting =
    upiIntentsEnabled &&
    paymentStatus?.type === 'UPI_INTENT' &&
    paymentStatus?.status === PaymentStatus.AWAITING &&
    !isError

  useEffect(() => {
    hasTriggeredUpiIntentRef.current = false
    setShowUpiRedirectModal(false)
  }, [orderId])

  useEffect(() => {
    const actionUrl = paymentStatus?.actionUrl

    if (!isUpiIntentAwaiting || !actionUrl || hasTriggeredUpiIntentRef.current) {
      if (!isUpiIntentAwaiting) {
        setShowUpiRedirectModal(false)
      }
      return
    }

    setShowUpiRedirectModal(true)

    const redirectTimeoutId = window.setTimeout(() => {
      hasTriggeredUpiIntentRef.current = true
      upiPay({
        upiLink: actionUrl,
        packageName: paymentStatus?.upiPackageName,
      })
      setShowUpiRedirectModal(false)
    }, 1000)

    return () => window.clearTimeout(redirectTimeoutId)
  }, [isUpiIntentAwaiting, paymentStatus?.actionUrl, paymentStatus?.upiPackageName])

  const handleBackButtonClick = () => {
    openBottomSheet(
      <PaymentNotCompletedBottomSheet
        onDismiss={() => {
          closeBottomSheet()
        }}
        onStayOnScreen={() => {
          closeBottomSheet()
        }}
        onExitAnyway={() => {
          closeBottomSheet()
          postCTA(JSON.stringify(getOrdersTabCTA()))
        }}
        ScreenName='BuyUPIPaymentStatusPage'
      />
    )
  }

  useEffect(() => {
    if (isError || paymentStatus?.status === PaymentStatus.FAILED) {
      closeBottomSheet()
      setStatus(TerminalStatus.FAILURE)
    } else if (paymentStatus?.status === PaymentStatus.SUCCESS) {
      closeBottomSheet()
      setStatus(TerminalStatus.FULL_SUCCESS)
    } else if (paymentStatus?.status === PaymentStatus.AWAITING) {
      setStatus(TerminalStatus.AWAITING)
    }
  }, [paymentStatus, isError])

  useEffect(() => {
    if (isExpired && (status === TerminalStatus.LOADING || status === TerminalStatus.AWAITING)) {
      closeBottomSheet()
      setStatus(TerminalStatus.PENDING)
    }
  }, [isExpired, status])

  return (
    <div className='w-full h-screen relative overflow-hidden'>
      {upiIntentsEnabled && showUpiRedirectModal ? (
        <UpiIntentRedirectModal
          upiPackageName={paymentStatus?.upiPackageName}
          actionUrl={paymentStatus?.actionUrl}
        />
      ) : null}
      <AllStatusWrapper
        status={status}
        statusContext={TerminalStatusContext.BUY}
        loadingConfig={{
          title: 'Awaiting payment',
          caption: `This screen will timeout in ${convertMillisecondsToMinutesAndSeconds(timeLeft)} <br />  Do not press back or close this screen.`
        }}
        awaitingConfig={{
          subtitle: upiIntentsEnabled ? `Complete payment using ${displayAccNumber}. <br/ > This may take a few seconds.` : '',
          title: upiIntentsEnabled
            ? `Confirming your <br /> payment status`
            : `Go to your UPI app and complete payment using ${displayAccNumber}`,
          timerText: convertMillisecondsToMinutesAndSeconds(timeLeft),
          caption: 'Do not close this screen',
          onBackButtonClick: handleBackButtonClick
        }}
        fullSuccessConfig={{
          title: 'Order placed',
          caption: 'Your investment will be processed <br /> within 3-4 working days.',
          ctas: [
            {
              ctaType: 'secondary',
              label: 'Go to dashboard',
              onClick: () => {
                // Handle navigation to dashboard
                console.log('Navigate to dashboard')
                postCTA(JSON.stringify(getOrdersTabCTA()))
              }
            }
          ]
        }}
        failureConfig={{
          title: 'Payment failed',
          caption: 'Any amount deducted will be credited to <br /> source within 2-4 working days.',
          ctas: [
            {
              ctaType: 'primary',
              label: 'Close',
              onClick: () => {
                // Handle navigation to dashboard
                console.log('Navigate to dashboard')
                postCTA(JSON.stringify(getOrdersTabCTA()))
              }
            }
          ]
        }}
        pendingConfig={{
          title: 'Payment pending',
          caption: 'It’s taking longer than expected. We’ll <br /> notify you once your payment is confirmed.',
          ctas: [
            {
              ctaType: 'primary',
              label: 'Close',
              onClick: () => {
                // Handle navigation to dashboard
                console.log('Navigate to dashboard')
                postCTA(JSON.stringify(getOrdersTabCTA()))
              }
            }
          ]
        }}
      />
    </div>
  )
}
